import { useState } from 'react'
import { cn } from '~shared/utils/cn'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'

// ─── Types ────────────────────────────────────────────────────────────────────

type TFullProgramContentsProps = {
  program: Awaited<ReturnType<typeof getProgramData>>
}

type ContentSubPartType = {
  id: string
  subPartTitle: string
  subPartDescription: string
  lines: Array<{ text: string; id: string }>
}

type ContentPartType = {
  id: string
  partTitle: string
  isBonusPart: boolean
  subParts: ContentSubPartType[]
}

// ─── getRgbFromHex ────────────────────────────────────────────────────────────

const getRgbFromHex = (hex: string) => {
  hex = hex.replace(/^#/, '')
  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `${r}, ${g}, ${b}`
}

// ─── ArrowDownIcon ────────────────────────────────────────────────────────────

const ArrowDownIcon = ({ isOpen }: { isOpen: boolean }) => (
  <div className={cn('transition-transform duration-200', isOpen && 'rotate-180')}>
    <svg width='36' height='36' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg'
      className='group'>
      <circle cx='18' cy='18' r='18' fill='#F2F2F2' className='group-hover:fill-black transition-colors' />
      <path
        d='M18.1314 22.6278C18.3407 22.6278 18.5499 22.5441 18.6923 22.385L25.1711 15.7472C25.3134 15.6049 25.3971 15.4208 25.3971 15.2115C25.3971 14.7762 25.0706 14.4414 24.6354 14.4414C24.4261 14.4414 24.2336 14.5251 24.0912 14.659L17.6794 21.2132H18.5751L12.1632 14.659C12.0293 14.5251 11.8368 14.4414 11.6191 14.4414C11.1839 14.4414 10.8574 14.7762 10.8574 15.2115C10.8574 15.4208 10.9411 15.6049 11.0834 15.7556L17.5622 22.385C17.7213 22.5441 17.9138 22.6278 18.1314 22.6278Z'
        fill='black'
        className='group-hover:fill-white transition-colors'
      />
    </svg>
  </div>
)

// ─── Chip ─────────────────────────────────────────────────────────────────────

const Chip = ({ label, color }: { label: string; color: string | null }) => (
  <span
    className='inline-flex items-center px-[16px] md:px-[16px] 2lg:px-[16px] py-[8px] rounded-[32px] text-white text-[16px] md:text-[16px] 2lg:text-[20px] leading-[120%]'
    style={{ backgroundColor: color ?? undefined }}>
    {label}
  </span>
)

// ─── Accordion ────────────────────────────────────────────────────────────────

const Accordion = ({
  subPart,
  isBonusPart,
  color
}: {
  subPart: ContentSubPartType
  isBonusPart: boolean
  color: string
}) => {
  const { subPartTitle, subPartDescription, lines } = subPart
  const hasLines = lines.length > 0
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className={cn(
        'border border-[#F2F2F2] text-black',
        'first:rounded-t-[16px] last:rounded-b-[16px]',
        'text-[16px] md:text-[16px] 2lg:text-[24px] leading-[120%]'
      )}
      style={{
        backgroundColor: isBonusPart
          ? `rgba(${getRgbFromHex(color)}, 0.2)`
          : '#fff'
      }}>

      {/* Summary */}
      <div
        className={cn(
          'flex items-center justify-between',
          hasLines ? 'cursor-pointer' : 'cursor-default'
        )}
        onClick={() => hasLines && setIsOpen(prev => !prev)}>
        <div className={cn(
          'flex-1 p-[16px] md:p-[16px] 2lg:p-[24px]',
          hasLines && !isOpen && 'border-r-2 border-[#F2F2F2]'
        )}>
          {subPartTitle}
        </div>
        {hasLines && (
          <div className='p-[15px] md:p-[15px] 2lg:p-[22px] shrink-0'>
            <ArrowDownIcon isOpen={isOpen} />
          </div>
        )}
      </div>

      {/* Details */}
      {hasLines && isOpen && (
        <div className='pb-[20px] md:pb-[20px] 2lg:pb-[24px] px-[4px]'>
          {subPartDescription && (
            <p className='text-[16px] md:text-[16px] 2lg:text-[22px] leading-[150%] px-[32px] md:px-[32px] 2lg:px-[40px] mt-0 mb-[8px]'>
              {subPartDescription}
            </p>
          )}
          <ul className='m-0 pl-[32px] md:pl-[32px] 2lg:pl-[40px]'>
            {lines.map(({ text, id }) => (
              <li key={id} className='text-[14px] md:text-[14px] 2lg:text-[20px] leading-[180%]'>
                {text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ─── ContentPart ──────────────────────────────────────────────────────────────

const ContentPart = ({
  part,
  color
}: {
  part: ContentPartType
  color: string | null
}) => {
  const { partTitle, isBonusPart, subParts } = part

  return (
    <div className='[&+&]:mt-[24px] 2lg:[&+&]:mt-[64px]'>
      {/* Mobile/Tablet: plain colored box, Desktop: chip */}
      <div
        className='md:hidden inline-block text-white text-[16px] leading-[120%] rounded-[8px] px-[16px] py-[8px]'
        style={{ backgroundColor: color ?? undefined }}>
        {partTitle}
      </div>
      <div className='hidden md:inline-block'>
        <Chip label={partTitle} color={color} />
      </div>

      <div className='w-full mt-[16px] 2lg:mt-[32px]'>
        {subParts.map(subPart => (
          <Accordion
            key={subPart.id}
            subPart={subPart}
            isBonusPart={isBonusPart}
            color={color ?? ''}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FullProgramContents = ({ program }: TFullProgramContentsProps) => {
  const fullProgramContents = program?.fullProgramContents ?? null
  const color = program?.faculty?.color ?? null
  const heading = program?.name ?? ''

  if (!fullProgramContents) return null

  const {
    description,
    durationInMonths,
    contentHours,
    workshopsHours,
    supervisionHours,
    bulletsTitle,
    bullets,
    parts,
    durationInMonthsTitle,
    contentHoursTitle,
    workshopsHoursTitle,
    supervisionHoursTitle
  } = fullProgramContents

  const stats = [
    { title: durationInMonthsTitle || 'Месяцев обучения', value: durationInMonths },
    { title: contentHoursTitle || 'Часов обучения', value: contentHours },
    { title: workshopsHoursTitle || 'Часов практикумов', value: workshopsHours },
    { title: supervisionHoursTitle || 'Часов супервизий', value: supervisionHours }
  ]

  return (
    <>
      {/* ─── Hero section with gradient ─── */}
      <section
        className='py-[56px] 2lg:py-0 relative'
        style={{
          background: `linear-gradient(to right, ${color} 65%, #fff 65%)`
        }}>
        {/* Mobile bg override */}
        <div
          className='2lg:hidden absolute inset-0'
          style={{ backgroundColor: color ?? undefined }}
        />

        <div className='relative mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
          <div className='flex flex-col 2lg:flex-row 2lg:justify-between gap-[32px] 2lg:gap-[68px] text-white'>

            {/* Left */}
            <div className='2lg:py-[80px] 2lg:max-w-[700px]'>
              <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-medium leading-[120%] mb-[24px] 2lg:mb-[48px]'>
                Программа обучения
              </h2>
              <p className='text-[16px] 2lg:text-[20px] leading-[140%] mb-[24px] 2lg:mb-[48px]'>
                {description}
              </p>

              {/* Stats box */}
              <div className='flex flex-wrap 2lg:flex-nowrap gap-[20px] 2lg:gap-0 p-[24px] rounded-[16px] bg-white text-black w-full'>
                {stats.map(({ title, value }, idx) => (
                  <div
                    key={title}
                    className={cn(
                      '2lg:flex-1',
                      idx > 0 && '2lg:pl-[24px] 2lg:border-l 2lg:border-black 2lg:ml-[5px]'
                    )}>
                    <div className='text-[40px] 2lg:text-[48px] leading-[100%] font-normal mb-[7px]'>
                      {value}
                    </div>
                    <div className='text-[14px] leading-[120%] font-medium'>
                      {title}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: bullets */}
            <div
              className='2lg:w-[35%] rounded-[16px] p-[24px] 2lg:p-[80px_0px_80px_80px] text-black bg-white box-border'>
              <h3 className='text-[20px] 2lg:text-[24px] font-medium leading-[120%] mb-[16px] 2lg:mb-[24px]'>
                {bulletsTitle}
              </h3>
              <div className='flex flex-wrap gap-[8px]'>
                {bullets.map(({ text, id }) => (
                  <Chip key={id} label={text} color={color} />
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── Parts / Accordion section ─── */}
      <section
        className='py-[56px] 2lg:py-[64px] bg-[#F2F2F2]'>
        <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
          {parts.map(part => (
            <ContentPart key={part.id} part={part} color={color} />
          ))}
        </div>
      </section>
    </>
  )
}